
import _ from "lodash";
import rest from "@/rest";
import { Component, Vue, Watch, Prop, PropSync } from "vue-property-decorator";
import { Action, namespace } from "vuex-class";
import { List } from "linq-collections";
import { vuex } from "@/store";
import VueQrcode from "@chenfengyuan/vue-qrcode"; // https://github.com/fengyuanchen/vue-qrcode
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { VCardInfoVm } from "@/interfaces/VCardInfoVm";
// component

// interfaces


const auth = namespace("auth");
const ux = namespace("ux");
const globals = namespace("globals");

@Component({
  components: {
    qrcode: VueQrcode
  }
})
export default class VCardDlg extends Vue {
  @PropSync("showDialog") syncedShowDialog!: boolean;

  @auth.Getter loggedIn!: boolean;
  @ux.State darkTheme: any;
  @globals.Getter vCard!: VCardInfoVm;
  @globals.Action UpdateVCard: any;

  busyOnSave = false;
  busyOnDelete = false;
  vCardEdit: VCardInfoVm | null = null;
  editMode = false;
  formValid = false;
  showLocalStorageInfo = false;

  windowWidth = window.innerWidth;
  windowHeight = window.innerHeight;
  qrCodeWidth = 370;
  debouncedResize = _.debounce(this.onResize, 250, { leading: true, trailing: true});

  $refs!: {
    backgroundVideo: any;
    formElement: HTMLFormElement;
    qrcodeCanvas: HTMLCanvasElement;
  };

  mounted() {
    window.addEventListener('resize', this.debouncedResize);
    this.onResize();

    // const video = this.$refs.backgroundVideo;
    // if (video) {
    //   video.addEventListener('error', (e) => {
    //     console.error('Video error:', e);
    //     // Fall back to a CSS background
    //     this.useVideoBg = false;
    //   });
    // }
  }

  beforeDestroy() {
    // Clean up event listener when component is destroyed
    window.removeEventListener('resize', this.debouncedResize);

    clearAllBodyScrollLocks();
    this.syncedShowDialog = false;
  }

  async onResize() {
    this.windowWidth = window.visualViewport?.width ?? 370;
    this.windowHeight = window.visualViewport?.height ?? 370;
    this.qrCodeWidth = Math.min(this.windowWidth, this.windowHeight);
    if (this.qrCodeWidth > 500)
      this.qrCodeWidth = 500;

    this.qrCodeWidth -= 45;
    await this.$globalHelper.delay(200);
    if (this.$refs.qrcodeCanvas)
      this.onReady((this.$refs.qrcodeCanvas as any).$el);
  }

  @Watch("syncedShowDialog")
  async onShowDialogChanged(val: boolean) {
    if (this.syncedShowDialog) {
      // On dialog open
      this.editMode = false;

      // Disable body scroll on iOS
      this.$nextTick(async () => {
        await this.$globalHelper.delay(10);
        const modal = document.querySelector('.modal');
        disableBodyScroll(modal);
      });
    } else {
      // Dialog closing
      clearAllBodyScrollLocks();
    }
  }

  @Watch("editMode")
  async onEditModeChanged(val: boolean) {
    if (val) {
      this.vCardEdit = _.cloneDeep(this.vCard);
    } else if (this.$refs.qrcodeCanvas) {
        await this.$globalHelper.delay(100);
        this.onReady((this.$refs.qrcodeCanvas as any).$el);
    }
  }

  async onSave() {
    if (!(await this.$refs.formElement.validate()))
      return;

    // this.$refs.formElement.reset();
    this.busyOnSave = true;
    await rest.url("public/vCardInfo")
      .post(this.vCardEdit)
      .then((response) => {
        this.UpdateVCard(response);
        this.editMode = false;
      })
      .finally(() => {
        this.busyOnSave = false;
      });
  }

  async onDelete() {
    this.busyOnDelete = true;
    await rest.url("public/vCardInfo")
      .delete()
      .then((response) => {
        this.UpdateVCard(null);
        this.$refs.formElement.reset();
        this.syncedShowDialog = false;
      })
      .finally(() => {
        this.busyOnDelete = false;
      });
  }

  onCancel() {
    clearAllBodyScrollLocks();
    this.syncedShowDialog = false;
  }

  onReady(canvas) {
    // console.log("QR Code ready", canvas);
    const context = canvas.getContext('2d');
    const image = new Image();

    image.src = 'Logo.png';
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      const coverage = 0.2;
      const width = Math.round(canvas.width * coverage);
      const x = (canvas.width - width) / 2;

      const imgWidth = image.width;
      const imgHeight = image.height;
      const aspect = imgWidth / imgHeight;
      let height = width / aspect;
      const y = (canvas.height - height) / 2;
      // console.log("QR Code image", image, x, y, width, height);

      this.drawImage(context, image, x, y, width, height);
    };
  }

  drawImage(context, image, x, y, width, height, radius = 3) {
    let canvasHeight = height;
    let canvasWidth = width;
    let canvasX = x;
    let canvasY = y;
    if (canvasHeight + 10 < canvasWidth) {
      canvasHeight += 10;
      canvasY -= 5;
    }

    context.shadowOffsetX = 0;
    context.shadowOffsetY = 2;
    context.shadowBlur = 4;
    context.shadowColor = '#00000040';
    context.lineWidth = 8;
    context.beginPath();
    context.moveTo(canvasX + radius, canvasY);
    context.arcTo(canvasX + canvasWidth, canvasY, canvasX + canvasWidth, canvasY + canvasHeight, radius);
    context.arcTo(canvasX + canvasWidth, canvasY + canvasHeight, canvasX, canvasY + canvasHeight, radius);
    context.arcTo(canvasX, canvasY + canvasHeight, canvasX, canvasY, radius);
    context.arcTo(canvasX, canvasY, canvasX + canvasWidth, canvasY, radius);
    context.closePath();
    context.strokeStyle = '#fff';
    context.stroke();
    context.clip();
    context.fillStyle = '#fff';
    context.fillRect(canvasX, canvasY, canvasWidth, canvasHeight);
    context.drawImage(image, x, y, width, height);
  }

  get showVideoBackground() {
    if (!this.editMode && this.vCard) return this.vCard.showBackgroundVideo;
    if (this.editMode && this.vCardEdit) return this.vCardEdit.showBackgroundVideo;

    return true;
  }

  get disableSaveButton() {
    return false;
  }

  get accentColor() {
    return this.$store.state.ux.colorAccent;
  }

  get contentBackgroundColor() {
    if (this.darkTheme)
      return "#00000040";
    else
      return "#ffffff60";
  }

  // Rules
  prefixRules = [
    (v: string) => (!v || (v && v.length <= 20)) || "Titel zu lang"
  ];

  firstNameRules = [
    (v: string) => !!v || "Name erforderlich",
    (v: string) => (v && v.length > 1) || "Name zu kurz",
    (v: string) => (v && v.length <= 50) || "Name zu lang"
  ];

  middleNameRules = [
    (v: string) => (!v || (v && v.length <= 80)) || "Namen zu lang"
  ];

  lastNameRules = [
    (v: string) => !!v || "Name erforderlich",
    (v: string) => (v && v.length > 1) || "Name zu kurz",
    (v: string) => (v && v.length <= 150) || "Name zu lang"
  ];

  organizationRules = [
    (v: string) => (!v || v.length > 1) || "Name zu kurz",
    (v: string) => (!v || (v && v.length <= 150)) || "Name zu lang"
  ];

  jobTitleRules = [
    (v: string) => (!v || (v && v.length <= 100)) || "Titel zu lang"
  ];

  roleRules = [
    (v: string) => (!v || (v && v.length <= 100)) || "Titel zu lang"
  ];

  mailRules = [
    // (v: string) => !!v || "E-Mail Adresse ist erforderlich",
    (v: string) =>
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$|^$/.test(
        v
      ) || "Keine gültige E-Mail Adresse",
    (v: string) => (!v ||  v.length <= 150) || "E-Mail Adresse zu lang"
  ];

  phoneRules = [
  (v: string) => {
    // Handle empty string explicitly first
    if (!v) return true;

    // Then check phone number pattern only if not empty
    return /^(\+\d{1,3})?[-.\s]?\(?\d{1,4}\)?[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/.test(v) ||
      "Keine gültige Telefonnummer";
  },
  (v: string) => (!v || v.length <= 30) || "Telefonnummer zu lang"  ];

  urlRules = [
    (v: string) => (!v || (v && v.length <= 250)) || "URL zu lang"
  ];

  streetRules = [
    (v: string) => (!v || (v && v.length <= 100)) || "Straßenname zu lang"
  ];

  zipRules = [
    (v: string) => (!v || (v && v.length <= 20)) || "Postleitzahl zu lang"
  ];

  cityRules = [
    (v: string) => (!v || (v && v.length <= 100)) || "Stadtname zu lang"
  ];

  stateRules = [
    (v: string) => (!v || (v && v.length <= 50)) || "Bundesland/Region zu lang"
  ];

  countryRules = [
    (v: string) => (!v || (v && v.length <= 50)) || "Land zu lang"
  ];
}
